import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    InjectionToken,
    Optional,
    Output,
} from "@angular/core";

declare var tinymce;
import "tinymce/tinymce";

export const TINYMCE_URL = new InjectionToken<string>("TINYMCE_URL");

@Component({
    selector: "tinymce",
    templateUrl: "./tinymce.component.html",
})
export class TinyMCEComponent implements OnChanges, AfterViewInit, OnDestroy {

    @Input() readonly: boolean = false;
    @Input() id: string;
    @Input() options: any = {};
    @Output() contentChange = new EventEmitter();
    @Output() blur = new EventEmitter();
    @Output() focus = new EventEmitter();

    baseURL: string = "";

    private editor: any = null;
    public initialValue: string = "";

    constructor(private zone: NgZone, @Inject(TINYMCE_URL) @Optional() TINYMCE_URL: string) {
        this.baseURL = TINYMCE_URL ? TINYMCE_URL : "//cdnjs.cloudflare.com/ajax/libs/tinymce/4.4.2";
    }

    ngAfterViewInit() {
        this.createEditor();
    }

    ngOnChanges(changes) {
        if (this.editor && changes["readonly"]) {
            this.removeEditor();
            this.createEditor();
        }
    }

    // TinyMCE triggers an error if the DOM elements it created are
    // inexistent which is the case if this component if located in
    // a structural block (*ngIf/*ngFor).
    // We indicate the editor has already been removed from DOM
    // so that destroy doesn"t call "remove" and we remove the editor
    // from tinymce.
    ngOnDestroy() {
        if (this.editor) {
            this.editor.removed = true;
            this.removeEditor();
        }
    }

    private createEditor() {
        tinymce.baseURL = this.baseURL;
        const options: any = {
            selector: "#" + this.id,
            readonly: this.readonly ? 1 : 0,
            // plugins: ['link', 'paste', 'table', 'code'],
            // menubar: "insert",
            plugins: 'advlist paste table code autolink link image lists charmap',
            toolbar: 'undo redo | cut copy paste | styleselect formatselec fontselect removeforma | bold italic underline strikethrough subscript superscript| alignleft aligncenter alignright alignjustify | link bullist numlist outdent indent| code list charmap', /* image */
            skin_url: 'assets/skins/lightgray',

            setup: (editor) => { this.editor = editor; this.bindEditor(); },
        };

        Object.assign(options, this.options);

        if (this.readonly) {
            options.toolbar = options.menubar = false;
        }
        tinymce.init(options);
    }

    private bindEditor() {
        this.editor.on("change keyup cut paste undo redo", () => {
            this.emitContentChange();
        });

        this.editor.on("blur", () => {
            this.zone.run(() => { this.blur.emit(true); return true; });
        });

        this.editor.on("focus", () => {
            this.zone.run(() => { this.focus.emit(true); });
        });
    }

    removeEditor() {
        // Hacks to avoid exceptions if the editor is destroyed
        // before it is thoroughly initialized
        if (!this.editor.selection) {
            this.editor.selection = { destroy: () => { } };
            this.editor.selection.dom = {};
        }

        if (!this.editor.dom) {
            this.editor.dom = { destroy: () => { } };
        }

        this.editor.destroy();
        tinymce.remove(this.editor);
        this.editor = null;
    }

    private emitContentChange() {
        const event = { value: this.editor.getContent() };
        this.zone.run(() => {
            this.contentChange.emit(event);
        });
    }

    setContent(content: string) {
        this.initialValue = content;
        if (this.editor) {
            this.editor.setContent(content);
        }
    }
}
